import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    ConfirmDialogInput,
    ConfirmDialogResult,
} from '../../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import { EnterValueDialogComponent } from '../../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-dialog.component';
import {
    EnterValueInput,
    EnterValueResponse,
} from '../../../../../projects/kiene-core/src/lib/components/dialogs/enter-value-dialog/enter-value-response';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { Befundmitteilung } from '../../../laboratory/befundmitteilung/befundmitteilung';
import { Invoice } from '../rechnung';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import {
    Abgabebeleg,
    AbgabebelegStatus,
} from '../../../../../projects/kiene-core/src/lib/components/abgabebelege/classes/abgabebeleg';
import { AbgabebelegPosition } from '../../../../../projects/kiene-core/src/lib/components/abgabebelege/classes/abgabebeleg-position';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { KieneUtils } from '../../../../../projects/kiene-core/src/lib/utils/KieneUtils';

export class StornoResult {
    abgabebelege: Abgabebeleg[] = [];
    befunde: Befundmitteilung[] = [];
}

@Component({
    selector: 'kiene-rechnung-belege-dialog',
    templateUrl: './rechnung-belege-dialog.component.html',
    styleUrls: ['./rechnung-belege-dialog.component.scss'],
})
export class RechnungBelegeDialogComponent implements OnInit {
    invoice: Invoice;
    stornoResult: StornoResult;

    formControlsArtikelmenge: UntypedFormControl[] = [];
    formControlsArtikelpreise: UntypedFormControl[] = [];

    constructor(
        public dialogRef: MatDialogRef<RechnungBelegeDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private readonly data,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneTableService,
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        private readonly localStorageService: LocalStorageService,
    ) {
        if (this.data) {
            this.invoice = this.data.invoice;
        }
    }

    ngOnInit(): void {
        this.loadBelege();
    }

    refresh() {
        this.loadBelege();
    }

    loadBelege() {
        this.api
            .getOne(this.apiBaseUrl + 'rechnung/read_storno.php', 'rechnung_id', this.invoice?.rechnung_id?.toString())
            .subscribe({
                next: response => {
                    this.stornoResult = <StornoResult>response;
                    this.prepareFormControls();
                },
                error: err => {
                    this.messageService.errorMessage(
                        `Die Belege für die Rechnung ${this.invoice.rechnung_nr} konnten nicht geladen werden`,
                        err,
                    );
                },
            });
    }

    prepareFormControls() {
        for (let ab of this.stornoResult.abgabebelege) {
            for (let p of ab.positionen) {
                this.formControlsArtikelmenge[p.aa_id] = new UntypedFormControl(p.artikelmenge);
                this.formControlsArtikelpreise[p.aa_id] = new UntypedFormControl(p.einzelpreis);
            }
        }
    }

    reopenAbgabebeleg(a: Abgabebeleg) {
        this.api.post(this.apiBaseUrl + 'abgabebeleg/korrigieren.php', a, null).subscribe({
            next: () => {
                // this.openAbgabebelegInNewTab(response);
                this.loadBelege();
            },
            error: err => {
                this.loadBelege();
                this.messageService.errorMessage(
                    'Der Abgabebeleg ' + a.abgabebeleg_nr + ' konnte nicht wieder geöffnet werden!',
                    err,
                );
            },
        });
    }

    canReopenAbgabebeleg(a: Abgabebeleg): boolean {
        if (!this.localStorageService.hasPermission(1245)) {
            return false;
        }
        const currentUser = this.localStorageService.getCurrentUser();
        if (!currentUser) {
            return false;
        }
        if (currentUser.admin > 0 || !currentUser.tierarzt_id) {
            return true;
        }
        return KieneUtils.sameId(a?.tierarzt_id, currentUser.tierarzt_id);
    }

    abgabebelegStatusIs(a: Abgabebeleg, ...statusIds: number[]): boolean {
        return statusIds.some(id => KieneUtils.sameId(a?.status_id, id));
    }

    isAbgabebelegEditable(a: Abgabebeleg): boolean {
        return this.abgabebelegStatusIs(a, 41, 46);
    }

    isAbgabebelegStornierbar(a: Abgabebeleg): boolean {
        return KieneUtils.sameId(a?.status_id, 42);
    }

    befundStatusIs(b: Befundmitteilung, statusId: number): boolean {
        return KieneUtils.sameId(b?.status_id, statusId);
    }

    updateEinzelpreisAbgabebelegposition(p: AbgabebelegPosition) {
        const neuerPreis = this.formControlsArtikelpreise[p.aa_id].value;
        if (neuerPreis === p.einzelpreis) {
            return;
        }
        p.einzelpreis = neuerPreis;
        this.api.updateGeneric(this.apiBaseUrl + 'abgabebeleg/update_einzelpreis.php', p).subscribe({
            next: () => {
                this.messageService.infoMessage('Der Einzelpreis wurde erfolgreich aktualisiert!');
                this.loadBelege();
            },
            error: err => {
                this.messageService.errorMessage('Der Einzelpreis konnte nicht aktualisiert werden!', err);
            },
        });
    }

    updateArtikelmengeAbgabebelegposition(p: AbgabebelegPosition) {
        const neueMenge = this.formControlsArtikelmenge[p.aa_id].value;
        if (neueMenge === p.artikelmenge) {
            return;
        }
        p.artikelmenge = neueMenge;
        this.api.updateGeneric(this.apiBaseUrl + 'abgabebeleg/update_artikelmenge.php', p).subscribe({
            next: () => {
                this.messageService.infoMessage('Die Artikelmenge wurde erfolgreich aktualisiert!');
                this.loadBelege();
            },
            error: err => {
                this.messageService.errorMessage('Die Artikelmenge konnte nicht aktualisiert werden!', err);
            },
        });
    }

    deletePosition(a: Abgabebeleg, index: number) {
        a.positionen.splice(index, 1);
        this.api.updateEntity(this.apiBaseUrl + 'abgabebeleg/update.php', a).subscribe({
            next: () => {
                this.messageService.infoMessage('Die Position wurde erfolgreich entfernt!');
                this.loadBelege();
            },
            error: err => {
                this.messageService.errorMessage('Die Position konnte nicht entfernt werden!', err);
            },
        });
    }

    lockAbgabebeleg(a: Abgabebeleg) {
        a.status_id = AbgabebelegStatus.ABGESCHLOSSEN;
        this.api.updateEntity(this.apiBaseUrl + 'abgabebeleg/update.php', a).subscribe({
            next: () => {
                this.messageService.infoMessage('Der Beleg wurde erfolgreich abgeschlossen!');
                this.loadBelege();
            },
            error: err => {
                this.loadBelege();
                this.messageService.errorMessage(
                    'Der Abgabebeleg ' + a.abgabebeleg_nr + ' konnte nicht abgeschlossen werden!',
                    err,
                );
            },
        });
    }

    openAbgabebelegInNewTab(a: Abgabebeleg) {
        const url = this.router.serializeUrl(this.router.createUrlTree(['/belege/abgabebelege', a.abgabebeleg_id]));
        window.open(url, '_blank');
    }

    reopenBefund(b: Befundmitteilung) {
        this.api.post(this.apiBaseUrl + 'befund/korrigieren.php', b, null).subscribe({
            next: () => {
                this.openBefundInNewTab(b);
                this.loadBelege();
            },
            error: err => {
                this.messageService.errorMessage(
                    'Der Befund ' + b.tagebuch_nr + ' konnte nicht wieder geöffnet werden!',
                    err,
                );
            },
        });
    }

    openBefundInNewTab(b: Befundmitteilung) {
        const url = this.router.serializeUrl(this.router.createUrlTree(['/labor/befundmitteilungen', b.befund_id]));
        window.open(url, '_blank');
    }

    close() {
        this.dialogRef.close();
    }

    openCreateNewInvoiceDialog() {
        const input = new EnterValueInput();
        input.title = 'Rechnung erstellen';
        input.text = 'Bitte geben Sie noch das Rechnungsdatum ein, welches die neue Rechnung haben soll.';
        input.okButton = 'Rechnung erstellen';
        input.buttonColor = 'accent';
        input.addInputValue('Rechnungsdatum', 'date', this.invoice.rechnungsdatum);

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: EnterValueResponse) => {
                if (result?.result && result?.getValue(0)) {
                    const obj = { rechnung_id: this.invoice.rechnung_id, rechnungsdatum: result.getValue(0) };

                    this.api.post(this.apiBaseUrl + 'rechnung/generieren_neu.php', obj, null).subscribe({
                        next: response => {
                            this.messageService.infoMessage('Es wurde eine neue Rechnung erstellt');
                            this.router.navigate(['/finanzen', 'rechnungen', response.rechnung_id]);
                            this.dialogRef.close();
                        },
                        error: err => {
                            this.messageService.errorMessage(
                                'Beim Erstellen der neuen Rechnung ist ein Fehler aufgetreten!',
                                err,
                            );
                        },
                    });
                }
            },
        });
    }

    openRemoveAbgabebelegDialog(a: Abgabebeleg) {
        const input = new ConfirmDialogInput();
        input.headline = 'Abgabebeleg aus Rechnung entfernen';
        input.text =
            'Soll der Abgabebeleg aus der Rechnung entfernt werden? Dabei wird dieser Abgabebelg zuerst dupliziert und anschließend storniert. Beim Duplizieren werden die HIT/QS Status der einzelnen Positionen übernommen.';
        input.okButton = 'Aus Rechnung entfernen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    // erst duplizieren und danach den orginalen stornieren
                    this.duplicateAbgabebeleg(a).subscribe({
                        next: () => {
                            this.cancelAbgabebeleg(a);
                        },
                        error: err => {
                            this.messageService.errorMessage(
                                'Fehler beim Duplizieren des Abgabebelegs ' + a.abgabebeleg_nr,
                                err,
                            );
                        },
                    });
                }
            },
        });
    }

    duplicateAbgabebeleg(abgabebeleg: Abgabebeleg) {
        return this.api.post(this.apiBaseUrl + 'abgabebeleg/duplicate.php', abgabebeleg, undefined);
    }

    cancelAbgabebeleg(a: Abgabebeleg) {
        this.api.post(this.apiBaseUrl + 'abgabebeleg/stornieren.php', a, null).subscribe({
            next: () => {
                this.messageService.infoMessage(`Der Abgabebeleg ${a.abgabebeleg_nr} wurde erfolgreich storniert!`);
                this.loadBelege();
            },
            error: err => {
                this.messageService.errorMessage(
                    `Der Abgabebeleg ${a.abgabebeleg_nr} konnte nicht storniert werden!`,
                    err,
                );
            },
        });
    }

    cancelBefund(b: Befundmitteilung) {
        this.api.post(this.apiBaseUrl + 'befund/stornieren.php', b, null).subscribe({
            next: () => {
                this.messageService.infoMessage(`Der Befund ${b.tagebuch_nr} wurde erfolgreich storniert!`);
                this.loadBelege();
            },
            error: err => {
                this.messageService.errorMessage(`Der Befund ${b.tagebuch_nr} konnte nicht storniert werden!`, err);
            },
        });
    }

    duplicateBefund(b: Befundmitteilung) {
        return this.api.post(this.apiBaseUrl + 'befund/duplizieren.php', b, null);
    }

    openRemoveBefundDialog(b: Befundmitteilung) {
        const input = new ConfirmDialogInput();
        input.headline = 'Befundmitteilung aus Rechnung entfernen';
        input.text =
            'Soll die Befundmitteilung aus der Rechnung entfernt werden? Dabei wird diese Befundmitteilung zuerst dupliziert und anschließend storniert.';
        input.okButton = 'Aus Rechnung entfernen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    // erst duplizieren und danach den orginalen stornieren
                    this.duplicateBefund(b).subscribe({
                        next: () => {
                            this.cancelBefund(b);
                        },
                        error: err => {
                            this.messageService.errorMessage(
                                'Fehler beim Duplizieren der Befundmitteilung ' + b.tagebuch_nr,
                                err,
                            );
                        },
                    });
                }
            },
        });
    }
}
